import { useState } from 'react';
import { useConnectionMode } from '@/connection/SimpleConnectionProvider';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { SkeletonCard } from '@/components/skeleton/LiteModeSkeleton';
import { Skeleton } from '@/components/ui/skeleton';
import { Loader2, Eye, EyeOff } from 'lucide-react';

/**
 * Component to demonstrate skeleton loaders in different modes
 * Shows static skeletons for Lite, pulse animation for Full
 */
export const SkeletonShowcase = () => {
  const mode = useConnectionMode();
  const [showSkeletons, setShowSkeletons] = useState(true);
  const isLite = mode === 'lite' || mode === 'offline';

  if (import.meta.env.PROD) return null; // Only show in development

  return (
    <Card className="border-2 border-dashed">
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <span className="flex items-center gap-2">
            <Loader2 className="w-5 h-5" />
            Skeleton Loader Demo - {mode.toUpperCase()}
          </span>
          <div className="flex items-center gap-2">
            <Badge variant={isLite ? 'secondary' : 'default'}>
              {isLite ? 'Static CSS' : 'CSS Pulse'}
            </Badge>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowSkeletons(!showSkeletons)}
            >
              {showSkeletons ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </Button>
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {showSkeletons ? (
          <>
            <div className="space-y-2">
              <div className="text-sm font-medium">Lite Mode Skeleton (Static Shimmer):</div>
              <SkeletonCard />
            </div>

            <div className="space-y-2">
              <div className="text-sm font-medium">UI Skeleton (CSS Pulse):</div>
              <div className="flex items-center space-x-4 p-4 bg-muted/50 rounded-lg">
                <Skeleton className="h-12 w-12 rounded-full" />
                <div className="space-y-2 flex-1">
                  <Skeleton className="h-4 w-3/4" />
                  <Skeleton className="h-4 w-1/2" />
                </div>
              </div>
            </div>

            <div className="p-3 bg-yellow-50 dark:bg-yellow-950 rounded-lg">
              <div className="text-sm font-medium mb-1">🎯 Key Differences</div>
              <ul className="text-xs text-muted-foreground space-y-1">
                <li><strong>Lite Mode:</strong> Static shimmer effect using CSS gradients (no animation property)</li>
                <li><strong>Full Mode:</strong> CSS pulse animation with animate-pulse class</li>
                <li><strong>Performance:</strong> Both are GPU-accelerated and battery-efficient</li>
                <li><strong>Bundle:</strong> No JS animation libraries - pure CSS only</li>
              </ul>
            </div>

            <div className="p-3 bg-green-50 dark:bg-green-950 rounded-lg">
              <div className="text-sm font-medium mb-1">✅ Verification Checklist</div>
              <ul className="text-xs text-muted-foreground space-y-1">
                <li>✓ No framer-motion in Network tab</li>
                <li>✓ Skeletons load instantly (no JS)</li>
                <li>✓ Animations use CSS transforms only</li>
                <li>✓ Works in airplane mode (cached CSS)</li>
              </ul>
            </div>
          </>
        ) : (
          <div className="text-center py-8 text-muted-foreground">
            Click the eye icon to show skeleton demo
          </div>
        )}
      </CardContent>
    </Card>
  );
};
